import React, { useState, useEffect } from 'react';
import s from './BurgerMenu.module.scss';
import { NavLink } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import { FaPhone, FaEnvelope, FaTelegram, FaWhatsapp } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import Logo from './../../assets/icons/logoHeader.svg';
import { ReactComponent as MaxIcon } from './../../assets/icons/maxIcon.svg';

interface BurgerMenuProps {
    isOpen: boolean;
    onToggle: () => void;
}

const BurgerMenu: React.FC<BurgerMenuProps> = ({ isOpen, onToggle }) => {
    const { info } = useAppSelector(state => state.info);

    const handleLinkClick = () => {
        onToggle();
    };

    // Блокируем прокрутку body когда меню открыто
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    const menuVariants = {
        closed: {
            x: '100%',
            transition: {
                type: 'tween',
                duration: 0.3,
                ease: 'easeInOut'
            }
        },
        open: {
            x: 0,
            transition: {
                type: 'tween',
                duration: 0.3,
                ease: 'easeInOut'
            }
        }
    };

    const navItemVariants = {
        closed: { opacity: 0, x: 50 },
        open: (i: number) => ({
            opacity: 1,
            x: 0,
            transition: {
                delay: i * 0.1,
                duration: 0.3
            }
        })
    };

    const contactItemVariants = {
        closed: { opacity: 0, y: 20 },
        open: (i: number) => ({
            opacity: 1,
            y: 0,
            transition: {
                delay: 0.4 + i * 0.05,
                duration: 0.3
            }
        })
    };

    return (
        <>
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        className={s.overlay}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        onClick={onToggle}
                    />
                )}
            </AnimatePresence>

            <motion.div
                className={s.wrapper}
                variants={menuVariants}
                initial="closed"
                animate={isOpen ? 'open' : 'closed'}
            >
                <div className={s.content}>
                    <div className={s.header}>
                        <img className={s.logo} src={Logo} alt='logo' />
                        <button
                            type="button"
                            className={s.closeButton}
                            aria-label="Закрыть меню"
                            onClick={onToggle}
                        >
                            <span />
                            <span />
                        </button>
                    </div>
                    <nav className={s.nav}>
                        <ul>
                            {[
                                { to: '/', label: 'Главная' },
                                { to: '/projects', label: 'Проекты' },
                                { to: '/products', label: 'Продукция' },
                                { to: '/about-us', label: 'О нас' },
                                { to: '/contacts', label: 'Контакты' }
                            ].map((item, i) => (
                                <motion.li
                                    key={item.to}
                                    custom={i}
                                    variants={navItemVariants}
                                    initial="closed"
                                    animate={isOpen ? 'open' : 'closed'}
                                >
                                    <NavLink
                                        onClick={handleLinkClick}
                                        className={({ isActive }) => isActive ? s.active : ''}
                                        to={item.to}
                                    >
                                        {item.label}
                                    </NavLink>
                                </motion.li>
                            ))}
                        </ul>
                    </nav>

                    <div className={s.contacts}>
                        <motion.div
                            className={s.contactsPhone}
                            custom={0}
                            variants={contactItemVariants}
                            initial="closed"
                            animate={isOpen ? 'open' : 'closed'}
                        >
                            <a rel="noreferrer noopener" href={`tel:${info?.contacts?.phone}`}>
                                {React.createElement(FaPhone as any)}
                                <span>{info?.contacts?.phone}</span>
                            </a>
                            <a rel="noreferrer noopener" href={`mailto:${info?.contacts?.email}`}>
                                {React.createElement(FaEnvelope as any)}
                                <span>{info?.contacts?.email}</span>
                            </a>
                        </motion.div>

                        <motion.div
                            className={s.socialLinks}
                            custom={1}
                            variants={contactItemVariants}
                            initial="closed"
                            animate={isOpen ? 'open' : 'closed'}
                        >
                            <a target='_blank' rel="noreferrer noopener" href={`${info?.contacts?.telegram}`}>
                                {React.createElement(FaTelegram as any)}
                            </a>
                            <a target='_blank' rel="noreferrer noopener" href={`${info?.contacts?.whatsapp}`}>
                                {React.createElement(FaWhatsapp as any)}
                            </a>
                            {info?.contacts?.vk && (
                                <a target='_blank' rel="noreferrer noopener" href={`${info?.contacts?.vk}`}>
                                    {React.createElement(MaxIcon as any)}
                                </a>
                            )}
                        </motion.div>
                    </div>
                </div>
            </motion.div>
        </>
    );
};

export default BurgerMenu;
